import { announceSessionChange } from './session-boundary';
import { t } from '../messages';
/**
 * La **seule** porte réseau du bureau. Aucun `fetch(` nu ailleurs dans `src/**`.
 *
 * Deux garanties, posées ici une fois pour toutes :
 *
 * 1. **Liste blanche de chemins.** Avant même d'appeler `fetch`, un chemin hors de `/api/rpc/`,
 *    `/api/files/`, `/api/realtime` et `/api/auth/` est refusé. Le bureau ne peut donc pas, même
 *    par erreur de frappe, appeler une adresse qui n'est pas une porte déclarée de l'API.
 * 2. **En-tête CSRF systématique.** L'API refuse toute mutation sans `x-ndrc-rpc` **et** sans un
 *    `Origin` égal à `PUBLIC_ORIGIN` (`apps/api/src/csrf.ts`). L'`Origin` est posé par le
 *    navigateur ; l'en-tête, lui, est posé ici — jamais oublié par un appelant.
 *
 * Toutes les URL sont **relatives** : le bureau est servi par la même origine que l'API (proxy
 * Vite en développement, Caddy en classe). Il n'y a donc aucune adresse d'API à configurer, et
 * aucun `process.env` à lire.
 */

export const ALLOWED_API_PREFIXES = [
  '/api/rpc/',
  // Sans barre oblique finale, contrairement à `/api/rpc/` : il y a DEUX adresses de fichiers, et
  // l'une d'elles n'a pas de segment après `files` — `POST /api/files` (le dépôt) et
  // `GET /api/files/<id>?exp&sig` (l'ouverture). Écrit `'/api/files/'`, ce garde refusait le dépôt
  // avant même d'appeler `fetch`, et l'écran affichait « Le bureau n'a pas pu se charger » sans
  // qu'aucune requête ne parte — constaté dans un vrai navigateur le 8 septembre 2026.
  '/api/files',
  '/api/images/',
  '/api/practice-files',
  '/api/realtime',
  '/api/auth/',
  '/api/external/',
  '/api/exams/',
  '/api/server-health',
] as const;

/** En-tête personnalisé exigé par le garde CSRF de l'API (`apps/api/src/csrf.ts`). */
const CSRF_HEADER = 'x-ndrc-rpc';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Vrai quand l'erreur signifie « plus de session » — le seul cas qui doit ramener à l'écran de
 * connexion, jamais un 403 (qui, lui, veut dire « connecté, mais pas le droit »). */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function assertAllowedPath(path: string): void {
  if (!ALLOWED_API_PREFIXES.some((prefix) => path.startsWith(prefix))) {
    throw new Error(
      `Chemin d'API interdit : « ${path} ». Le bureau n'appelle que ${ALLOWED_API_PREFIXES.join(', ')}.`,
    );
  }
}

interface ApiErrorBody {
  error?: { code?: unknown; message?: unknown; details?: unknown };
}
function requestAborted(signal: AbortSignal, error: unknown): never {
  // Preserve the existing French timeout message for uploads/health reads. An actual query
  // cancellation is rethrown unchanged, so leaving a class does not surface a network alert.
  const reason: unknown = signal.reason;
  if (reason instanceof DOMException && reason.name === 'TimeoutError')
    throw new ApiError('NETWORK', t('network.unavailable'), 0);
  throw error;
}

async function apiFetch(path: string, init: RequestInit): Promise<unknown> {
  assertAllowedPath(path);
  // Un envoi de fichier (`FormData`) ne doit PAS porter `content-type: application/json` : c'est
  // le navigateur qui pose `multipart/form-data; boundary=…`, et il ne peut le faire que si
  // personne n'a fixé l'en-tête avant lui. L'en-tête CSRF, lui, reste posé dans les deux cas —
  // c'est ce que le garde de `apps/api/src/csrf.ts` exige de TOUTE mutation, fichier compris.
  const isUpload = init.body instanceof FormData;
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(isUpload ? undefined : { 'content-type': 'application/json' }),
        [CSRF_HEADER]: '1',
        ...init.headers,
      },
    });
  } catch (error) {
    // Query cancellation must remain a cancellation, not a visible network failure. Timed
    // commands are reconciled by their caller, which keeps the original idempotency key.
    if (init.signal?.aborted) requestAborted(init.signal, error);
    // Coupure réseau, API arrêtée, proxy absent : un message qui dit quoi faire, jamais
    // « TypeError: Failed to fetch » sous les yeux d'un enseignant.
    throw new ApiError('NETWORK', t('network.unavailable'), 0);
  }

  const raw: unknown = await response.json().catch((error: unknown) => {
    if (init.signal?.aborted) requestAborted(init.signal, error);
    return null;
  });
  if (!response.ok) {
    const body = raw as ApiErrorBody | null;
    const code = typeof body?.error?.code === 'string' ? body.error.code : 'INTERNAL_ERROR';
    const message =
      typeof body?.error?.message === 'string'
        ? body.error.message
        : 'Une erreur interne est survenue.';
    throw new ApiError(code, message, response.status, body?.error?.details);
  }
  return raw;
}

/* --- Le fil temps réel -----------------------------------------------------------------------
   `GET /api/realtime/poll?since=` — la seule lecture du bureau qui ne passe pas par le pipeline
   RPC. Elle ne prend AUCUN identifiant : ni classe, ni élève, ni canal. Ce qu'un onglet peut lire
   est décidé par la session et par la RLS de `core.realtime_log`, jamais par ce qu'il demande —
   voir `apps/api/src/routes/realtime.ts`. */

/** Un signal, tel que le cœur le rend. Redéclaré ici comme toutes les formes du réseau : le bureau
 * ne dépend d'aucun paquet `@ndrc/core-*`. */
export interface RealtimeSignal {
  rtSeq: string;
  type: string;
  workspaceId: string | null;
}

export interface RealtimePollResult {
  head: string;
  signals: RealtimeSignal[];
  resync: boolean;
}

export async function pollRealtime(
  since: string | null,
  signal: AbortSignal,
): Promise<RealtimePollResult> {
  const query = since === null ? '' : `?since=${encodeURIComponent(since)}`;
  return (await apiFetch(`/api/realtime/poll${query}`, {
    method: 'GET',
    signal,
  })) as RealtimePollResult;
}

/** Une **query** du cœur : `POST /api/rpc/core/<op>`, sans clé d'idempotence (l'API n'en exige que
 * pour les mutations). */
export async function callCore<T>(
  op: string,
  input: Record<string, unknown> = {},
  signal?: AbortSignal,
): Promise<T> {
  return (await apiFetch(`/api/rpc/core/${op}`, {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
  })) as T;
}

/**
 * Une **mutation** du cœur. La clé d'idempotence est générée ici, jamais laissée au bon vouloir de
 * l'appelant : sans elle, l'API refuse la mutation (`IDEMPOTENCY_KEY_REQUIRED`) — et avec elle, un
 * double clic ou une page rechargée ne produit ses effets qu'une seule fois.
 */
export async function mutateCore<T>(
  op: string,
  input: Record<string, unknown> = {},
  idempotencyKey: string = crypto.randomUUID(),
  sourceAppId?: string,
  signal?: AbortSignal,
): Promise<T> {
  return (await apiFetch(`/api/rpc/core/${op}`, {
    method: 'POST',
    body: JSON.stringify(input),
    signal,
    headers: {
      'idempotency-key': idempotencyKey,
      ...(sourceAppId ? { 'x-ndrc-source-app': sourceAppId } : {}),
    },
  })) as T;
}

export async function login(identifier: string, password: string): Promise<void> {
  await apiFetch('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
  });
  announceSessionChange('login');
}

export async function logout(): Promise<void> {
  try {
    await apiFetch('/api/auth/logout', { method: 'POST', body: '{}' });
  } finally {
    // Even an uncertain server response must not leave another tab displaying private work.
    announceSessionChange('logout');
  }
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiFetch('/api/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

/**
 * Import d'une liste de classe (`POST /api/auth/teacher/import/preview` puis `/import/commit`,
 * `apps/api/src/routes/teacher.ts`). Deux étapes distinctes, jamais fusionnées : `preview` ne crée
 * rien (l'enseignant voit d'abord qui sera créé, les anomalies du fichier et les élèves déjà
 * importés), `commit` crée les comptes et renvoie la fiche d'identifiants imprimable.
 *
 * Le fichier est renvoyé une seconde fois au `commit` — c'est voulu : ces deux routes ne
 * conservent aucun état entre elles, donc rien de périmé ne peut s'appliquer à l'insu de
 * l'enseignant.
 */
export interface ImportAnomaly {
  rowNumber: number;
  reason: string;
}

/**
 * Ce que l'aperçu affiche d'un candidat. `initialPassword` fait bien partie de la réponse de
 * `preview` (`buildImportPlan` le calcule pour tout candidat) mais n'est **pas déclaré ici** : ce
 * mot de passe n'est celui d'aucun compte tant que `commit` n'a pas eu lieu — `commit` rejoue le
 * plan et en génère de nouveaux. L'afficher dans l'aperçu ferait noter à l'enseignant des mots de
 * passe qui ne seront jamais valides.
 */
export interface ImportCandidatePreview {
  firstName: string;
  lastName: string;
  studentCode: string;
  group?: string;
}

export interface ImportPreviewResult {
  candidates: ImportCandidatePreview[];
  anomalies: ImportAnomaly[];
  alreadyImported: Array<{ firstName: string; lastName: string }>;
}

export interface CreatedStudentResult {
  userId: string;
  firstName: string;
  lastName: string;
  studentCode: string;
  initialPassword: string;
}

export interface ImportCommitResult extends ImportPreviewResult {
  students: CreatedStudentResult[];
  credentialsHtml: string;
  credentialsCsv: string;
}

export async function importStudents(
  step: 'preview',
  classId: string,
  file: File,
): Promise<ImportPreviewResult>;
export async function importStudents(
  step: 'commit',
  classId: string,
  file: File,
): Promise<ImportCommitResult>;
export async function importStudents(
  step: 'preview' | 'commit',
  classId: string,
  file: File,
): Promise<ImportPreviewResult | ImportCommitResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('classId', classId);
  return (await apiFetch(`/api/auth/teacher/import/${step}`, {
    method: 'POST',
    body: form,
  })) as ImportPreviewResult | ImportCommitResult;
}

/* --- Les documents ---------------------------------------------------------------------------
   Le DÉPÔT d'un document est la seule mutation du bureau qui ne passe pas par le pipeline RPC : il
   transporte des octets (`multipart/form-data`), pas du JSON. La lecture (`files.list`) et le
   retrait (`files.remove`), eux, sont des opérations du cœur comme les autres — voir
   `lib/queries.ts`. */

export interface UploadedDocument {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
  createdAt: string;
  /** L'adresse signée par laquelle CETTE session peut l'ouvrir. Voir `@ndrc/core-files`. */
  url: string;
  /** `true` : ce document était déjà déposé dans cette classe — rien n'a été créé, et l'écran le
   * dit plutôt que d'annoncer un dépôt qui n'a pas eu lieu. */
  deduplicated: boolean;
}

/**
 * Dépose un document pour une classe (`POST /api/files`). L'en-tête CSRF est posé par `apiFetch`
 * (comme pour toute mutation) ; le `content-type` ne l'est PAS, c'est le navigateur qui écrit la
 * frontière multipart — voir le commentaire d'`apiFetch`.
 */
export async function uploadDocument(classId: string, file: File): Promise<UploadedDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('classId', classId);
  return (await apiFetch('/api/files', { method: 'POST', body: form })) as UploadedDocument;
}
export async function uploadPracticeFile(
  classId: string,
  file: File,
): Promise<Omit<UploadedDocument, 'createdAt'>> {
  const form = new FormData();
  form.append('file', file);
  form.append('classId', classId);
  return (await apiFetch('/api/practice-files', {
    method: 'POST',
    body: form,
  })) as Omit<UploadedDocument, 'createdAt'>;
}

export interface ResetPasswordResult {
  studentCode: string | null;
  newPassword: string;
}

/**
 * Réinitialise le mot de passe d'un élève (`POST /api/auth/teacher/reset-password`). Le nouveau mot
 * de passe n'existe QUE dans cette réponse : rien ne le conserve en clair, et la route ne le
 * journalise pas (`apps/api/src/routes/teacher.ts`). C'est la seule façon honnête de « réimprimer
 * une fiche » — l'ancien mot de passe est irrécupérable par construction, pas par oubli.
 */
export async function resetStudentPassword(userId: string): Promise<ResetPasswordResult> {
  return (await apiFetch('/api/auth/teacher/reset-password', {
    method: 'POST',
    body: JSON.stringify({ userId }),
  })) as ResetPasswordResult;
}

export interface ExternalAccessForm {
  url: string;
  fields: Record<string, string>;
  expiresInSeconds: number;
}
/** Ephemeral credentials bypass the query cache and are never persisted. */
export async function requestExternalAccess(
  environmentId: string,
  signal?: AbortSignal,
): Promise<ExternalAccessForm> {
  return (await apiFetch('/api/external/open', {
    method: 'POST',
    body: JSON.stringify({ environmentId }),
    cache: 'no-store',
    signal,
  })) as ExternalAccessForm;
}

/** ADR-055: the host captures the target class/session and workspace generation. */
export async function uploadWorkspaceImage(
  target: {
    appId: string;
    classId: string;
    generation: number;
    missionRunId?: string;
  },
  file: File,
): Promise<import('@ndrc/app-sdk/client').WorkspaceImage> {
  const form = new FormData();
  form.append('file', file);
  form.append('generation', String(target.generation));
  return (await apiFetch(`/api/images/apps/${encodeURIComponent(target.appId)}`, {
    signal: AbortSignal.timeout(60000),
    method: 'POST',
    body: form,
    headers: {
      'x-ndrc-class': target.classId,
      ...(target.missionRunId ? { 'x-ndrc-mission-run': target.missionRunId } : {}),
    },
  })) as import('@ndrc/app-sdk/client').WorkspaceImage;
}

export async function readServerHealth<T>(signal?: AbortSignal): Promise<T> {
  return (await apiFetch('/api/server-health', {
    method: 'GET',
    signal: signal ?? AbortSignal.timeout(8000),
  })) as T;
}

/** Exam credentials do not create or replace a normal NDRC account session. */
export async function loginExam(
  examId: string,
  identifier: string,
  password: string,
): Promise<ExternalAccessForm & { hostname: string }> {
  return (await apiFetch(`/api/exams/${encodeURIComponent(examId)}/login`, {
    method: 'POST',
    body: JSON.stringify({ identifier, password }),
    cache: 'no-store',
  })) as ExternalAccessForm & { hostname: string };
}
